import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@app/providers/useAuth';
import { useTheme } from '@app/providers/ThemeProvider';
import { Home, GraduationCap, Settings, LogOut, Sun, Moon, User, Map, FolderGit2 } from 'lucide-react';
import NavigationSection from './NavigationSection';
import { ROUTES } from '@shared/config/routes';
import '@shared/styles/sidebar.css';

interface SidebarProps {
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
  onExpandedChange?: (expanded: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isMobileOpen, onMobileClose, onExpandedChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const isActive = (path: string): boolean => location.pathname === path;

  const handleMouseEnter = (): void => {
    setIsExpanded(true);
    onExpandedChange?.(true);
  };

  const handleMouseLeave = (): void => {
    setIsExpanded(false);
    setIsMenuOpen(false);
    onExpandedChange?.(false);
  };

  const educationItems = [
    { path: ROUTES.AI_TYPING, label: 'AI Typing' },
    { path: ROUTES.NOVA, label: 'Nova Tutor' },
    { path: ROUTES.PROBLEMS, label: 'Problems' },
  ];

  const roadmapItems = [
    { path: ROUTES.CALENDAR, label: 'Calendar' },
    { path: ROUTES.MY_ROADMAPS, label: 'My Roadmaps' },
  ];

  const sandboxItems = [
    { path: ROUTES.SANDBOX, label: 'New Project' },
    { path: ROUTES.REPOSITORY, label: 'My Repository' },
  ];

  const handleNavClick = (): void => {
    if (window.innerWidth <= 767) {
      onMobileClose?.();
    }
  };

  const handleMenuToggle = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogout = (): void => {
    void logout();
    navigate('/');
    setIsMenuOpen(false);
  };

  const handleThemeToggle = (): void => {
    toggleTheme();
  };

  const handleProfileClick = (): void => {
    navigate('/profile');
    setIsMenuOpen(false);
    if (window.innerWidth <= 767) {
      onMobileClose?.();
    }
  };

  const handleSettingsClick = (): void => {
    navigate('/settings');
    setIsMenuOpen(false);
    if (window.innerWidth <= 767) {
      onMobileClose?.();
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <>
      {/* Mobile Backdrop */}
      {isMobileOpen && (
        <div 
          className="sidebar-backdrop sidebar-backdrop--visible"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}
      
      <aside 
        className={`sidebar ${isMobileOpen ? 'sidebar--mobile-open' : ''} ${isExpanded ? 'sidebar--expanded' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <nav className="sidebar__nav">
          <ul className="sidebar__nav-list">
            {/* Home Link */}
            <li>
              <Link
                to={ROUTES.HOME}
                className={`sidebar__nav-item ${isActive(ROUTES.HOME) ? 'sidebar__nav-item--active' : ''}`}
                onClick={handleNavClick}
              >
                <span className="sidebar__icon">
                  <Home size={20} />
                </span>
                <span className="sidebar__label">Home</span>
              </Link>
            </li>

            {/* Education Section */}
            <NavigationSection
              title="Education"
              icon={GraduationCap}
              items={educationItems}
              onNavigate={handleNavClick}
              isExpanded={isExpanded}
            />

            {/* Roadmap Section */}
            <NavigationSection
              title="Roadmap"
              icon={Map}
              items={roadmapItems}
              onNavigate={handleNavClick}
              isExpanded={isExpanded}
            />

            {/* Sandbox Section */}
            <NavigationSection
              title="Sandbox"
              icon={FolderGit2}
              items={sandboxItems}
              onNavigate={handleNavClick}
              isExpanded={isExpanded}
            />
          </ul>
        </nav>
        
        <div className="sidebar__footer" ref={menuRef}>
          <button 
            className="sidebar__settings"
            onClick={handleMenuToggle}
          >
            <div className="sidebar__settings-icon">
              <Settings size={20} />
            </div>
            <span className="sidebar__settings-label">Menu</span>
          </button>

          {isMenuOpen && (
            <div className="sidebar__menu-popup">
              <button 
                className="sidebar__menu-header sidebar__menu-header--clickable"
                onClick={handleProfileClick}
              >
                <div className="sidebar__menu-avatar">
                  {user?.username?.substring(0, 2).toUpperCase() || 'KT'}
                </div>
                <div className="sidebar__menu-user">
                  <div className="sidebar__menu-username">{user?.username || 'Developer'}</div>
                  <div className="sidebar__menu-role">View Profile</div>
                </div>
              </button>

              <div className="sidebar__menu-divider"></div>

              <button 
                className="sidebar__menu-item"
                onClick={handleProfileClick}
              >
                <User size={18} />
                <span>Profile</span>
              </button>

              <button 
                className="sidebar__menu-item"
                onClick={handleSettingsClick}
              >
                <Settings size={18} />
                <span>Settings</span>
              </button>

              <div className="sidebar__menu-divider"></div>

              <button 
                className="sidebar__menu-item"
                onClick={handleThemeToggle}
              >
                {theme === 'dark' ? <Moon size={18} /> : <Sun size={18} />}
                <span>{theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</span>
              </button>

              <div className="sidebar__menu-divider"></div>

              <button 
                className="sidebar__menu-item sidebar__menu-item--logout"
                onClick={handleLogout}
              >
                <LogOut size={18} />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
